/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { RootState } from 'store';

import { flightsFilterClear, flightsSetTraveler } from 'action-creators/flights';
import { hotelsFilterClear, hotelsSetTraveler } from 'action-creators/hotels';
import { fetchUserAccount } from 'actions/user';
import Header from 'components/Header';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import AutosSearch from 'containers/AutosSearch';
import FlightsSearch from 'containers/FlightsSearch';
import HotelsSearch from 'containers/HotelsSearch';
import Menu from 'containers/UserMenu';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import useUserAccount from 'hooks/useUserAccount';
import { GetAllBillingCompanyResponse } from 'models/billingCompany';
import { amplitudelogEvent, ViewTravelSearchScreenEvent } from 'services/amplitudeService';
import { getAllBillingCompanies } from 'services/billingCompanyService';
import { getAllowedTravelers, TravelArrangerUsersAllowed } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { dateFormatWeekend } from 'utils/generic';
import { goBackToApp } from 'utils/webViewUtils';

import { Screen, Title, Body, BookingType } from './styles';

enum BookingSwitchEnum {
  Flights = 'flights',
  Hotels = 'hotels',
  Autos = 'autos',
}

const Booking = () => {
  const { t } = useStoreTranslation();
  const toast = useToast();
  const history = useHistory<{ errorMessage?: string } | null>();
  const dispatch = useDispatch();
  const user = useUserAccount();

  const { traveler: flightTraveler } = useSelector((state: RootState) => state.flights);
  const { traveler: hotelTraveler } = useSelector((state: RootState) => state.hotels);

  const [userLoaded, setUserLoaded] = useState(false);
  const [allowedTravelers, setAllowedTravelers] = useState<TravelArrangerUsersAllowed[]>([]);
  const [isLoadingAllowedTravelers, setLoadingAllowedTravelers] = useState(true);
  const [isShow, setIsShow] = useState(false);
  const [currentSwitch, setCurrentSwitch] = useState(BookingSwitchEnum.Flights);
  const [branchCompanies, setBranchCompanies] = useState<GetAllBillingCompanyResponse[]>([]);
  const [isLoadingBranchCompanies, setIsLoadingBranchCompanies] = useState(false);
  const companyId = useSelector((state: RootState) => state.user.accountResult?.companyId);


  const handleBookingTypeChange = (type: BookingSwitchEnum) => {
    setCurrentSwitch(type);

    history.replace(
      `/booking/${type === BookingSwitchEnum.Hotels ? 'hotels' : type === BookingSwitchEnum.Flights ? 'flights' : 'autos'
      }`,
    );
  };
  const openBottomSheet = () => {
    setIsShow(true);
  };

  const closeBottomSheet = () => {
    setIsShow(false);
  };

  useEffect(() => {
    const fetch = async () => {
      await dispatch(fetchUserAccount());
      setUserLoaded(true);
    };
    fetch();
  }, [dispatch]);

  const updateFlightSelectedTraveler = useCallback((list: TravelArrangerUsersAllowed[]) => {
    const selected = list.find((traveler) => traveler.id === flightTraveler?.id);
    dispatch(flightsSetTraveler(selected));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateHotelSelectedTraveler = useCallback((list: TravelArrangerUsersAllowed[]) => {
    const selected = list.find((traveler) => traveler.id === hotelTraveler?.id);
    dispatch(hotelsSetTraveler(selected));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  enum BookingProductType {
    FLIGHTS = 'FLIGHTS',
    HOTELS = 'HOTELS',
    AUTOS = 'CAR_RENTAL',
  }
  const sendAmplitudeEvent = (productType: BookingProductType) => {
    //Amplitude
    const event: ViewTravelSearchScreenEvent = {
      SOURCE: 'TRAVEL_HOME',
      TRAVEL_PRODUCT_TYPE: productType,
    };

    amplitudelogEvent(amplitudeEvents.VIEW_TRAVEL_SEARCH_SCREEN, event, user);
  };

  useEffect(() => {
    const fetch = async () => {
      if (!userLoaded || !user?.isTravelArranger) {
        setLoadingAllowedTravelers(false);
        updateFlightSelectedTraveler([]);
        updateHotelSelectedTraveler([]);
        return;
      }

      const { data } = await getAllowedTravelers();

      setAllowedTravelers(data);

      updateFlightSelectedTraveler(data);
      updateHotelSelectedTraveler(data);

      setLoadingAllowedTravelers(false);
    };

    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userLoaded, updateFlightSelectedTraveler, updateHotelSelectedTraveler]);

  useEffect(() => {
    setIsLoadingBranchCompanies(true);
    getAllBillingCompanies(companyId ?? '', true).then((res) => {
      setBranchCompanies(res);
      setIsLoadingBranchCompanies(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (history.location.pathname === '/booking/hotels') {
      setCurrentSwitch(BookingSwitchEnum.Hotels);
      sendAmplitudeEvent(BookingProductType.HOTELS);
      return;
    }
    if (history.location.pathname === '/booking/autos') {
      setCurrentSwitch(BookingSwitchEnum.Autos);
      sendAmplitudeEvent(BookingProductType.AUTOS);
      return;
    }
    if (history.location.pathname === '/booking/flights') {
      sendAmplitudeEvent(BookingProductType.FLIGHTS);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.pathname]);

  useEffect(() => {
    if (history.location.state && history.location.state.errorMessage) {
      let message = '';
      const messageSplitted = history.location.state.errorMessage.split(' ');
      const formattedDate = dateFormatWeekend(messageSplitted[messageSplitted.length - 1]);
      message = t('flightsSearch.invalidDateError', { date: formattedDate });
      toast.add({ type: 'danger', message: message || t('defaultError.default') });
      history.replace('/booking', null);
    }
  }, [history.location.state]); // eslint-disable-line react-hooks/exhaustive-deps
  // TODO: check dependencies or refactor

  useEffect(() => {
    dispatch(flightsFilterClear());
    dispatch(hotelsFilterClear());
  }, [dispatch]);

  return (
    <Screen>
      <Mobile>
        <Header openMenu={openBottomSheet} goBack={goBackToApp} />
      </Mobile>

      <Desktop>
        <ScreenHeader title={t('booking.title')} />
      </Desktop>

      <Body>
        <Mobile>
          <Title>{t('booking.title')}</Title>
        </Mobile>

        <BookingType
          className={currentSwitch}
          options={[
            { label: t('booking.flights'), value: BookingSwitchEnum.Flights, testId: 'booking-flight-tab' },
            { label: t('booking.hotels'), value: BookingSwitchEnum.Hotels, testId: 'booking-hotel-tab' },
            { label: t('booking.autos'), value: BookingSwitchEnum.Autos, testId: 'booking-autos-tab' },
          ]}
          active={currentSwitch}
          onChange={handleBookingTypeChange}
        />
        {currentSwitch === BookingSwitchEnum.Flights ? (
          <FlightsSearch allowedTravelers={allowedTravelers}
            isLoadingAllowedTravelers={isLoadingAllowedTravelers}
            isLoadingBranchCompanies={isLoadingBranchCompanies}
            branchCompanies={branchCompanies} />
        ) : currentSwitch === BookingSwitchEnum.Hotels ? (
          <HotelsSearch
            allowedTravelers={allowedTravelers}
            isLoadingAllowedTravelers={isLoadingAllowedTravelers}
            isLoadingBranchCompanies={isLoadingBranchCompanies}
            branchCompanies={branchCompanies} />
        ) : (
          <AutosSearch
            allowedTravelers={allowedTravelers}
            isLoadingAllowedTravelers={isLoadingAllowedTravelers}
            isLoadingBranchCompanies={isLoadingBranchCompanies}
            branchCompanies={branchCompanies}
          />
        )}
      </Body>

      <Menu show={isShow} onClose={closeBottomSheet} />
    </Screen>
  );
};

export default Booking;
